<template>
  <section class="px-4 py-10 sm:px-6 lg:px-10 text-black" style="font-family: Verdana, sans-serif;">
    <div class="mx-auto space-y-6">
      <div class="space-y-2">
        <h1 class="text-3xl font-bold text-slate-900">Video catalog</h1>
        <p class="text-sm text-black">
          All catalog variants shown in the same card format as the video results.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span class="text-sm font-semibold text-slate-700">Region</span>
        <div class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1">
          <button
            v-for="option in regionOptions"
            :key="option.value"
            type="button"
            class="rounded-full px-4 py-1 text-sm font-semibold transition"
            :class="selectedRegion === option.value ? 'bg-emerald-600 text-white' : 'text-slate-700'"
            @click="selectedRegion = option.value"
          >
            {{ option.label }}
          </button>
        </div>
        <label class="inline-flex items-center gap-2 text-sm text-slate-700">
          <input v-model="debugMode" type="checkbox" class="h-4 w-4">
          Debug selections
        </label>
      </div>

      <div v-if="debugMode" class="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
        <div class="grid gap-4 lg:grid-cols-3">
          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">Products</p>
            <div class="max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white p-2 space-y-1">
              <label
                v-for="product in availableProducts"
                :key="product.key"
                class="flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  class="h-4 w-4"
                  :value="product.key"
                  v-model="debugSelection.selectedProducts"
                >
                {{ product.label }}
              </label>
            </div>
            <p v-if="selectionLimitExceeded" class="text-xs text-amber-700">
              Product limit is {{ productLimit }}. Extra selections are ignored.
            </p>
          </div>

          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">Languages</p>
            <div class="space-y-1">
              <label
                v-for="lang in languageOptions"
                :key="lang.value"
                class="flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  class="h-4 w-4"
                  :value="lang.value"
                  v-model="debugSelection.selectedLanguages"
                >
                {{ lang.label }}
              </label>
            </div>
          </div>

          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">Narrator</p>
            <div class="space-y-1">
              <label class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="debugSelection.narrator" type="radio" value="male" class="h-4 w-4">
                Male
              </label>
              <label class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="debugSelection.narrator" type="radio" value="female" class="h-4 w-4">
                Female
              </label>
            </div>
          </div>
        </div>

        <div v-if="selectedRegion === 'us'" class="grid gap-4 lg:grid-cols-3">
          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">Licensed states</p>
            <div class="max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white p-2 space-y-1">
              <label
                v-for="state in US_STATES"
                :key="state"
                class="flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  class="h-4 w-4"
                  :value="state"
                  v-model="debugSelection.licenses"
                >
                {{ state }}
              </label>
            </div>
          </div>

          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">CA license number</p>
            <input
              v-model="debugSelection.caLicenseNumber"
              type="text"
              class="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
              :disabled="!hasCalifornia"
              placeholder="Required if CA selected"
            >
            <p v-if="hasCalifornia && !debugSelection.caLicenseNumber" class="text-xs text-amber-700">
              CA selected — license number is required.
            </p>
          </div>
        </div>

        <div v-else class="grid gap-4 lg:grid-cols-3">
          <div class="space-y-2">
            <p class="text-sm font-semibold text-slate-700">Contracting</p>
            <div class="space-y-1">
              <label class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="debugSelection.contracting" type="radio" value="foresters" class="h-4 w-4">
                Foresters
              </label>
              <label class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="debugSelection.contracting" type="radio" value="cpp" class="h-4 w-4">
                Canada Protection Plan
              </label>
              <label class="flex items-center gap-2 text-sm text-slate-700">
                <input v-model="debugSelection.contracting" type="radio" value="both" class="h-4 w-4">
                Both
              </label>
            </div>
          </div>
        </div>

        <p class="text-xs text-slate-500">
          Results update immediately from the selections above.
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
        <div
          v-for="variant in catalog"
          :key="variant.id"
          class="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4"
        >
          <div class="flex items-start justify-between gap-2">
            <div>
              <p class="text-sm font-semibold text-slate-700">
                {{ variant.productLabel }}
              </p>
              <p class="text-xs uppercase tracking-wide text-emerald-700">
                {{ variant.languageLabel }}
              </p>
              <p class="text-xs text-slate-500">
                Narrator: {{ variant.narratorLabel }}
              </p>
              <p v-if="variant.version === 'ca'" class="text-[10px] uppercase tracking-wide text-amber-700">
                CA variant
              </p>
              <p class="text-[10px] uppercase tracking-wide text-slate-400">
                Region: {{ variant.regionLabel }}
              </p>
            </div>
          </div>

          <div class="rounded-lg text-xs text-slate-700 break-words">
            <img
              :src="variant.imageUrl"
              :alt="`${variant.productLabel} ${variant.languageLabel}`"
              class="w-full h-auto"
            >
          </div>

          <p v-if="variant.imageMissing" class="text-xs text-amber-700">
            No image provided
          </p>

          <div class="text-xs text-slate-700 break-words">
            <a
              :href="variant.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-emerald-700 underline"
            >
              {{ variant.url }}
            </a>
          </div>

          <div class="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 space-y-2 overflow-hidden">
            <div>
              <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Email subject
              </p>
              <p>{{ variant.email.subject }}</p>
            </div>
            <div>
              <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Email body
              </p>
              <div class="whitespace-pre-line">{{ variant.email.body }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { PRODUCT_LIMITS, PRODUCTS_US, PRODUCTS_CA, VIDEO_CATALOG } from '@/front/constants/catalog';
import { US_STATES, US_PRODUCT_RULES, CA_CONTRACTING_FILTERS, US_COMPLIANCE_CODES, US_VARIANT_LABELS } from '@/front/constants/rules';
import {
  EMAIL_TEMPLATE,
  PRODUCT_EMAIL_TEMPLATES,
  EMAIL_PRODUCT_DISCLOSURES,
  EMAIL_DISCLAIMERS,
  EMAIL_COMPLIANCE_CODES
} from '@/front/constants/copy';

const PLACEHOLDER_IMAGE =
  'https://www.biblecenterchurch.com/wp-content/uploads/2018/10/video-placeholder-4453_1080x675.png';

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Spanish',
  fr: 'French'
};

const NARRATOR_LABELS = {
  male: 'Male',
  female: 'Female'
};

const REGION_LABELS = {
  us: 'US',
  ca: 'CA'
};

const regionOptions = [
  { value: 'us', label: 'US' },
  { value: 'ca', label: 'Canada' }
];
const selectedRegion = ref('us');
const debugMode = ref(false);

const debugSelection = reactive({
  licenses: [],
  contracting: 'foresters',
  selectedProducts: [],
  selectedLanguages: ['en'],
  narrator: 'male',
  caLicenseNumber: ''
});

const SAMPLE_PERSON = {
  name: 'Sample Agent',
  phone: '111-111-1111',
  email: 'sample@example.com',
  producerId: '0000',
  caLicenseNumber: 'A1234567'
};

const buildUrlWithParams = (baseUrl, params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  return `${baseUrl}?${search.toString()}`;
};

const caLicenseNumberForVariant = (variant) => {
  if (variant.region !== 'us') return undefined;
  if (!hasCalifornia.value && variant.version !== 'ca') return undefined;
  return debugSelection.caLicenseNumber || SAMPLE_PERSON.caLicenseNumber;
};

const isUsProductHidden = (productKey, selectedStates = []) => {
  if (!selectedStates.length) return false;
  const blocked = US_PRODUCT_RULES[productKey]?.blockedStates || [];
  if (!blocked.length) return false;
  return selectedStates.every((state) => blocked.includes(state));
};

const hiddenCaProducts = (contracting) => {
  const rule = CA_CONTRACTING_FILTERS[contracting] || CA_CONTRACTING_FILTERS.both;
  return new Set(rule.hide);
};

const replaceToken = (value, token, replacement) => {
  const pattern = new RegExp(`\\[${token}\\]`, 'gi');
  return value.replace(pattern, replacement || `[${token}]`);
};

const applyTemplateValues = (text, productLabel, link, contactInfo, caLicenseNumber) => {
  const withProduct = replaceToken(
    replaceToken(text, 'Product Name', productLabel),
    'nom du produit',
    productLabel
  );
  const withSpanishProduct = replaceToken(withProduct, 'Nombre del Producto', productLabel);
  const withAgent = withSpanishProduct
    .replace(/\[Agent Name\]/g, SAMPLE_PERSON.name || '[Agent Name]')
    .replace(/\[Advisor Name\]/g, SAMPLE_PERSON.name || '[Advisor Name]')
    .replace(/\[Nom de l[’']agent\]/g, SAMPLE_PERSON.name || '[Nom de l’agent]')
    .replace(/\[Nom du conseiller\]/g, SAMPLE_PERSON.name || '[Nom du conseiller]')
    .replace(/\[Nombre del Agente\]/g, SAMPLE_PERSON.name || '[Nombre del Agente]')
    .replace(/\[Contact Info\]/g, contactInfo || '[Contact Info]')
    .replace(/\[Coordonnées\]/g, contactInfo || '[Coordonnées]')
    .replace(/\[Información de contacto\]/g, contactInfo || '[Información de contacto]');
  const withLink = replaceToken(
    replaceToken(withAgent, 'Insert Video Link', link),
    'lien de la vidéo',
    link
  );
  const withSpanishLink = replaceToken(withLink, 'Insertar enlace al video', link);
  return withSpanishLink.replace(/\[xxx\]/gi, caLicenseNumber || '[xxx]');
};

const buildEmail = (variant, url) => {
  const contactInfo = [SAMPLE_PERSON.phone, SAMPLE_PERSON.email]
    .filter(Boolean)
    .join('\n');
  const templates = PRODUCT_EMAIL_TEMPLATES?.[variant.region]?.[variant.language] || {};
  const template =
    templates[variant.productKey] ||
    EMAIL_TEMPLATE?.[variant.region]?.[variant.language] ||
    EMAIL_TEMPLATE?.us?.en;
  const subject = applyTemplateValues(
    template.subject,
    variant.productLabel,
    url,
    contactInfo,
    SAMPLE_PERSON.caLicenseNumber
  );
  const body = applyTemplateValues(
    template.body,
    variant.productLabel,
    url,
    contactInfo,
    SAMPLE_PERSON.caLicenseNumber
  );
  const bodyWithLink = body.includes(url) ? body : `${body}\n\n${url}`;
  const emailDisclaimers = EMAIL_DISCLAIMERS?.[variant.region]?.[variant.language];
  const productDisclosureEntry =
    EMAIL_PRODUCT_DISCLOSURES?.[variant.region]?.[variant.language]?.[variant.productKey];
  const productDisclosures =
    typeof productDisclosureEntry === 'object'
      ? productDisclosureEntry[variant.version] || productDisclosureEntry.standard
      : productDisclosureEntry;
  const complianceCode =
    EMAIL_COMPLIANCE_CODES?.[variant.region]?.[variant.language]?.[variant.productKey];
  const extraLines = [emailDisclaimers, productDisclosures, complianceCode]
    .filter(Boolean)
    .join('\n\n');
  return {
    subject,
    body: extraLines ? `${bodyWithLink}\n\n${extraLines}` : bodyWithLink
  };
};

const languageOptions = computed(() =>
  selectedRegion.value === 'us'
    ? [
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Spanish' }
    ]
    : [
      { value: 'en', label: 'English' },
      { value: 'fr', label: 'French' }
    ]
);

const productLimit = computed(() =>
  selectedRegion.value === 'us' ? PRODUCT_LIMITS.us : PRODUCT_LIMITS.ca
);

const hasCalifornia = computed(() =>
  selectedRegion.value === 'us' && debugSelection.licenses.includes('California')
);

const availableProducts = computed(() => {
  const base = selectedRegion.value === 'us' ? PRODUCTS_US : PRODUCTS_CA;
  if (selectedRegion.value !== 'us') {
    const hidden = hiddenCaProducts(debugSelection.contracting);
    return base.filter((product) => !hidden.has(product.key));
  }
  return base.filter((product) => !isUsProductHidden(product.key, debugSelection.licenses));
});

const selectionLimitExceeded = computed(
  () => debugSelection.selectedProducts.length > productLimit.value
);

const effectiveSelectedProducts = computed(() =>
  debugSelection.selectedProducts.slice(0, productLimit.value)
);

watch(
  () => selectedRegion.value,
  (region) => {
    debugSelection.licenses = [];
    debugSelection.contracting = 'foresters';
    debugSelection.selectedProducts = [];
    debugSelection.selectedLanguages = [region === 'us' ? 'en' : 'en'];
    debugSelection.narrator = 'male';
    debugSelection.caLicenseNumber = '';
  }
);

const buildComputedVariants = () => {
  const products = effectiveSelectedProducts.value;
  const languages = debugSelection.selectedLanguages;
  const variants = [];
  const hasCaliforniaLocal = hasCalifornia.value;

  products.forEach((productKey) => {
    const rule = US_PRODUCT_RULES[productKey];
    const versions = [];
    if (selectedRegion.value !== 'us' || !rule?.caVariants || !hasCaliforniaLocal) {
      versions.push('standard');
    } else {
      const blockedStates = rule?.blockedStates || [];
      const hasNonCaAllowed = debugSelection.licenses.some(
        (state) => state !== 'California' && !blockedStates.includes(state)
      );
      versions.push('ca');
      if (hasNonCaAllowed) {
        versions.push('standard');
      }
    }

    languages.forEach((language) => {
      versions.forEach((version) => {
        const match = VIDEO_CATALOG.find(
          (video) =>
            video.region === selectedRegion.value &&
            video.productKey === productKey &&
            video.language === language &&
            video.narrator === debugSelection.narrator &&
            (video.version || 'standard') === version
        );
        if (!match) return;

        const complianceCode =
          selectedRegion.value === 'us'
            ? US_COMPLIANCE_CODES?.[productKey]?.[version]?.[language]?.[
              debugSelection.narrator
            ] ||
            US_COMPLIANCE_CODES?.[productKey]?.standard?.[language]?.[
              debugSelection.narrator
            ]
            : undefined;

        variants.push({
          ...match,
          version,
          versionLabel:
            selectedRegion.value === 'us' ? US_VARIANT_LABELS[version] || version : undefined,
          complianceCode
        });
      });
    });
  });

  return variants;
};

const catalog = computed(() =>
  (debugMode.value ? buildComputedVariants() : VIDEO_CATALOG.filter(variant => variant.region === selectedRegion.value))
    .map((variant) => {
      const caLicenseNumber = caLicenseNumberForVariant(variant);
      const url = buildUrlWithParams(variant.baseUrl, {
        name: SAMPLE_PERSON.name,
        phone: SAMPLE_PERSON.phone,
        email: SAMPLE_PERSON.email,
        producer_id: SAMPLE_PERSON.producerId,
        product: variant.productKey,
        language: variant.language,
        region: variant.region,
        narrator: variant.narrator,
        ca_license_number: caLicenseNumber,
        license: caLicenseNumber
      });
      return {
        ...variant,
        languageLabel: LANGUAGE_LABELS[variant.language] || variant.language,
        narratorLabel: NARRATOR_LABELS[variant.narrator] || variant.narrator,
        regionLabel: REGION_LABELS[variant.region] || variant.region,
        imageUrl: variant.image || PLACEHOLDER_IMAGE,
        imageMissing: !variant.image,
        productKey: variant.productKey,
        url,
        email: buildEmail(variant, url)
      };
    })
);
</script>
